import { createFileRoute, Link } from "@tanstack/react-router";
import { Loader2, X } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@patient-watch/ui/alert";
import { Button } from "@patient-watch/ui/button";
import { ReportRenderPipelineDashboard } from "@/features/admin/components/ReportRenderPipelineDashboard";
import { sftpBreadcrumbTrail } from "@/features/admin/constants/sftpNav";
import { useReportRenderDashboard } from "@/features/admin/hooks/useReportRenderDashboard";
import { useNav } from "@/utils/nav";
import { z } from "zod";

const diariesSearchSchema = z.object({
    organisationId: z.string().uuid().optional(),
    organisationName: z.string().optional(),
});

function SftpDiariesPage() {
    const { organisationId, organisationName } = Route.useSearch();
    useNav(sftpBreadcrumbTrail("Diaries"));
    const { data, isLoading, isFetching, error, refetch } = useReportRenderDashboard({
        limit: 30,
        organisationId,
    });

    return (
        <div className="space-y-4 w-full min-w-0">
            <div>
                <h2 className="font-semibold text-lg">Diaries</h2>
                <p className="mt-1 text-muted-foreground text-sm">
                    {organisationId
                        ? "PDF render status for diaries in the selected organisation."
                        : "All diary PDFs on the SFTP pipeline: waiting, rendering, uploaded, and errors."}
                </p>
            </div>

            {organisationId && (
                <Alert className="bg-sky-50/60 border-sky-200">
                    <AlertTitle className="flex flex-wrap justify-between items-center gap-2">
                        <span>
                            Showing pipeline data for{" "}
                            <span className="font-semibold text-foreground">
                                {organisationName?.trim() || organisationId.slice(0, 8)}
                            </span>{" "}
                            only
                        </span>
                        <Button asChild variant="outline" size="sm" className="gap-2 bg-background">
                            <Link to="/developer/sftp/diaries">
                                <X className="w-4 h-4" />
                                Show all organisations
                            </Link>
                        </Button>
                    </AlertTitle>
                    <AlertDescription>
                        Summary cards, pipeline counts, and diary lists below are all scoped to this organisation.
                        <span className="block mt-1 font-mono text-xs">{organisationId}</span>
                    </AlertDescription>
                </Alert>
            )}

            {isLoading ? (
                <div className="flex justify-center items-center gap-2 py-16 text-muted-foreground">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Loading diary pipeline status...
                </div>
            ) : error ? (
                <div className="bg-rose-50 p-4 border border-rose-200 rounded-lg text-rose-900 text-sm">
                    Could not load diary pipeline status: {error instanceof Error ? error.message : "Unknown error"}
                </div>
            ) : data ? (
                <ReportRenderPipelineDashboard
                    data={data}
                    isFetching={isFetching}
                    onRefresh={() => void refetch()}
                    organisationFilter={
                        organisationId ? { organisationId, organisationName: organisationName ?? null } : undefined
                    }
                />
            ) : null}
        </div>
    );
}

export const Route = createFileRoute("/_app/developer/sftp/diaries")({
    validateSearch: (search) => diariesSearchSchema.parse(search),
    component: SftpDiariesPage,
});
